import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Box, Collapse, List, Stack, Typography } from '@mui/material';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { FormEditableText } from 'src/components/Forms';
import {
  NavigationSectionAdornmentWrapper,
  NavigationSectionContainer,
} from 'src/components/Navbar/navbar.styles';
import { NavigationDataItem } from 'src/components/Navbar/use-navigation-items';
import { CustomViewNavItem } from 'src/graphql/entities/custom-view';
import useNavigationStore from 'src/store/navigation';
import { useNotifications } from 'src/utils/hooks';
import { useScreenDimensions } from 'src/utils/system';
import { DraggableNavItem } from './draggable-nav-item';
import { StaticNavItem } from './static-nav-item';

const SectionIcon = ({ icon }: { icon: string }) => (
  <DynamicIcon name={icon} library="lucide" sx={{ marginLeft: '0.3rem' }} />
);

interface DrawerSectionProps {
  groupId: string;
  title: string;
  navItems: NavigationDataItem[];
  onClick?: (item: NavigationDataItem) => void;
  editTitle?: (newTitle: string) => void;
  isEditing?: boolean;
  rightAdornment?: ReactNode;
  /** Pins `rightAdornment` visible while its own menu is open — the menu's modal layer takes the pointer, so `:hover` drops. */
  isAdornmentPinned?: boolean;
  activeId?: string | null;
  activeSectionId?: string | null;
  isFolder?: boolean;
  icon?: string;
  onReorder?: (items: CustomViewNavItem[]) => Promise<void> | void;
}

export const DrawerSection = ({
  groupId,
  title,
  navItems,
  onClick,
  editTitle,
  isEditing,
  rightAdornment,
  isAdornmentPinned = false,
  onReorder,
  icon,
}: DrawerSectionProps) => {
  const { isMobile } = useScreenDimensions();
  const { sectionsOpenMap, setSectionOpen } = useNavigationStore();
  const [orderedItems, setOrderedItems] = useState(navItems);
  const { showErrorAlert } = useNotifications();

  const isOpen = sectionsOpenMap[groupId] ?? true;

  useEffect(() => {
    setOrderedItems(navItems);
  }, [navItems]);

  const toggleOpen = useCallback(
    () => setSectionOpen(groupId, !isOpen),
    [isOpen, setSectionOpen, groupId],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        // require a 5px movement before dragging
        distance: 5,
      },
    }),
  );

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;

      if (active.id !== over?.id && over) {
        // find indexes in the current orderedItems state
        const oldIndex = orderedItems.findIndex((item) => item.id === active.id);
        const newIndex = orderedItems.findIndex((item) => item.id === over.id);

        // optimistically update the state
        const newOrder = arrayMove(orderedItems, oldIndex, newIndex);
        setOrderedItems(newOrder);

        try {
          // call onReorder and await confirmation from the backend.
          // if it rejects, we revert to the previous order.
          // assertion simple narrows types down, it is safe operation here
          await onReorder?.(newOrder as CustomViewNavItem[]);
        } catch (error) {
          // roll back optimistic update on failure.
          setOrderedItems(navItems);
          showErrorAlert('Reordering failed');
          console.error('Reorder failed with error:', error);
        }
      }
    },
    [orderedItems, onReorder, navItems, showErrorAlert],
  );

  const itemIdentifiers = useMemo(() => orderedItems.map((item) => item.id), [orderedItems]);

  const renderList = () => {
    if (!onReorder || isMobile) {
      return orderedItems.map((item) => (
        <StaticNavItem key={item.id} item={item} onClick={onClick} />
      ));
    }

    return (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={itemIdentifiers} strategy={verticalListSortingStrategy}>
          {orderedItems.map((item) => (
            <DraggableNavItem
              key={item.id}
              id={item.id}
              item={item}
              sectionId={groupId}
              onClick={onClick}
              menuItems={item.menuItems}
            />
          ))}
        </SortableContext>
      </DndContext>
    );
  };

  const form = useForm({ defaultValues: { title } });

  const handleEditSubmit = useCallback(
    (data: { title: string }) => {
      if (editTitle && data.title && data.title !== title) {
        editTitle(data.title);
      }
    },
    [editTitle, title],
  );

  return (
    <Box flexShrink={0}>
      <Box>
        <NavigationSectionContainer>
          <Stack
            direction="row"
            justifyContent="space-between"
            width="100%"
            alignItems="center"
          >
            <Box
              onClick={toggleOpen}
              sx={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                <Box
                  sx={{
                    overflow: 'hidden',
                    minWidth: 0,
                    '& .MuiTypography-root': {
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      fontSize: '0.625rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      opacity: 0.8,
                    },
                  }}
                >
                  <FormProvider {...form}>
                    <FormEditableText
                      disableClickHandlers
                      isEditing={isEditing}
                      type="editable-text"
                      defaultValue={title}
                      disabled={!editTitle}
                      name="title"
                      onBlur={form.handleSubmit(handleEditSubmit)}
                      textFieldProps={{ inputProps: { maxLength: 200 } }}
                    />
                  </FormProvider>
                </Box>
                {icon ? <SectionIcon icon={icon} /> : null}
              </Box>
              <DynamicIcon
                name="arrow_drop_down"
                sx={{
                  transition: 'transform .2s',
                  transform: isOpen ? '' : 'rotate(-90deg)',
                  marginLeft: '1rem',
                }}
              />
            </Box>
            <NavigationSectionAdornmentWrapper pinned={isAdornmentPinned}>
              {rightAdornment}
            </NavigationSectionAdornmentWrapper>
          </Stack>
        </NavigationSectionContainer>
      </Box>
      <Collapse in={isOpen}>
        <List sx={{ padding: 0, margin: 0 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            {orderedItems.length > 0 ? (
              renderList()
            ) : (
              <Box>
                <Typography sx={{ paddingLeft: '2rem', paddingTop: '0.5rem' }} variant="body2">
                  No Records
                </Typography>
              </Box>
            )}
          </Box>
        </List>
      </Collapse>
    </Box>
  );
};

export default DrawerSection;
